import useRights from "@/shared/api/useRights";
import { Menu, MenuItem, Paper } from "@mui/material";
import { omit } from "lodash";
import Overdue from "./slots/Overdue";
import ProjectList from "./slots/ProjectList";
import { usePersistantStore } from "@/stores/global";
import { useMemo, useRef, useState } from "react";
import { ArrowDropDown } from "@mui/icons-material";
import Notifications, { NotificationButtons } from "./slots/Notifications";
import Service from "./slots/Service";
import Transfers from "./slots/Transfers";
import BauhofTransfers from "./slots/BauhofTransfers";
import Stoffstrom from "./slots/Stoffstrom";

interface SlotBase {
  name: string;
  key: string;
}

type SlotDefinition = SlotBase & { available: (rights: Record<string, string>) => boolean; button?: () => JSX.Element; render: () => JSX.Element };

const slots: SlotDefinition[] = [
  {
    name: "Benachrichtigungen",
    key: "notifications",
    button: () => <NotificationButtons />,
    available: (_rights) => true,
    render: () => <Notifications />,
  },
  {
    name: "Service",
    key: "service",
    available: (_rights) => true,
    render: () => <Service />,
  },
  {
    name: "Transfer-Historie",
    key: "transfers",
    available: (rights) => "DISPOSITION" in rights,
    render: () => <Transfers />,
  },
  {
    name: "Überfällige Planungen",
    key: "asphalt_overdue",
    available: (_rights) => true,
    render: () => <Overdue type="overdue" />,
  },
  {
    name: "Bereit zur Feinplanung",
    key: "asphalt_ready",
    available: (rights) => "DISPOSITION" in rights,
    render: () => <Overdue type="ready" />,
  },
  {
    name: "Meine Projekte",
    key: "my_projects",
    available: (_rights) => true,
    render: () => <ProjectList type="user" />,
  },

  {
    name: "Alle Projekte",
    key: "all_projects",
    available: (_rights) => true,
    render: () => <ProjectList type="all" />,
  },
  {
    name: "Mein Stoffstrom",
    key: "stoffstrom",
    available: (_rights) => true,
    render: () => <Stoffstrom />,
  },
  {
    name: "Bauhof-Transfers",
    key: "bauhof_transfers",
    available: (rights) => "DISPOSITION" in rights,
    render: () => <BauhofTransfers />,
  },
];

export function useAvailableSlots(withRender: boolean = false): SlotBase[] {
  const rights = useRights();
  return slots.flatMap((slot) => (slot.available(rights) ? [omit(slot, withRender ? ["available"] : ["available", "render"])] : []));
}

export default function Slot({ index }: { index: number }) {
  const availableSlots = useAvailableSlots(true);
  const panels = usePersistantStore((state) => state.panels);
  const setPanels = usePersistantStore((state) => state.setPanels);

  console.log("Panels", panels);

  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const currentSlot = useMemo(() => {
    const byKey = new Map(availableSlots.map((s: any) => [s.key, s]));
    const fixedKeys = new Set((panels ?? []).filter((k): k is string => !!k && byKey.has(k)));
    const freeQueue = availableSlots.filter((s: any) => !fixedKeys.has(s.key));

    let freePtr = 0;
    for (let i = 0; i <= index; i++) {
      const key = panels?.[i];
      if (key && byKey.has(key)) {
        if (i === index) return byKey.get(key);
        continue;
      }
      const slot = freeQueue[freePtr++];
      if (i === index) return slot;
    }
    return undefined;
  }, [availableSlots, panels, index]);

  const handleSelect = (key: string) => {
    setPanels(index, key);
    setMenuOpen(false);
  };

  const selectedKey = panels?.[index] ?? currentSlot?.key;

  return (
    <Paper sx={{ overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "5px 10px",
          justifyContent: "space-between",
          background: "#edf0f9",
          border: "1px solid rgba(0,0,0,0.2)",
          boxShadow: "inset 0px -4px 5px rgba(0,0,0,0.1)",

          userSelect: "none",
        }}
      >
        <div
          style={{
            textTransform: "uppercase",
            display: "flex",
            alignItems: "center",
            fontWeight: "bold",
            cursor: "pointer",
            color: "#555",
            fontSize: "15px",
          }}
          ref={anchorRef}
          onClick={() => setMenuOpen(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") setMenuOpen(true);
          }}
        >
          {currentSlot?.name || "Bitte wählen"}
          <ArrowDropDown />
        </div>
        {currentSlot?.button?.()}
      </div>

      <Menu
        anchorEl={anchorRef.current}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {availableSlots
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((s: any) => (
            <MenuItem key={s.key} selected={s.key === selectedKey} onClick={() => handleSelect(s.key)}>
              {s.name}
            </MenuItem>
          ))}
      </Menu>

      {currentSlot?.render()}
    </Paper>
  );
}
